import {
  Component,
  forwardRef,
  inject,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  ValidationErrors,
  AbstractControl,
  FormsModule,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CustomersService } from '../../../../customer/services/customers.service';
import {
  AvailableCredit,
  CustomerBalanceBreakdown,
  CustomerSettings,
  InvoiceModel,
  PaymentMethodMode,
} from '../../../../customer/customer.model';
import { MultiInvoiceCheckoutData, InvoiceCheckoutItem } from '../../../payment.model';
import { CreditApplication } from '../../../../transactions/transaction.model';
import { UserStore } from '../../../../user/store/user.store';
import { PaymentCalculatorService } from '../../../services/payment-calculator.service';
import { CostOfCapitalGuardService } from 'app/services/cost-of-capital-guard.service';
import { resolveCustomerCostOfCapital } from '../../../../customer/utils/customer-coc.util';
import {
  allocateCredits,
  allocateOldestFirst,
  compareDueDateOldestFirst,
  CreditAllocation,
  CreditShortfall,
  creditShortfalls,
} from './credit-allocation';

/**
 * Documents that can be paid here: true receivables. Card-fee documents are paid the
 * moment they are raised, so they never appear open.
 */
const PAYABLE_TYPES = ['ERPInvoice', 'AdhocInvoice', 'ERPFinanceCharges', 'ServiceFee'];
/** Of those, the ones that are not invoices — labelled so on the list. */
const NON_INVOICE_TYPES = new Set(['ERPFinanceCharges', 'ServiceFee']);
const TYPE_LABELS: Record<string, string> = {
  ERPFinanceCharges: 'Finance charge',
  ServiceFee: 'Service fee',
  ERPCreditMemo: 'Credit memo',
  ERPCreditOnFile: 'Credit on file',
  Coupon: 'Coupon',
  Adjustment: 'Adjustment',
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Collect Payment's "Customer Balance" step (invoice mode): the customer's balance at a
 * glance, their open charges oldest due first, every credit they can spend beside them, and —
 * where the profile allows it — an amount to put on the account instead of an invoice.
 *
 * Credit is applied by clicking it. Both kinds spend the same way — a credit document, and
 * money an operator posted straight to the balance with no document — because they arrive
 * from one read (`available-credits`) already filtered to what FTE would accept, identified
 * throughout by the `creditInvoiceRef` the apply call takes. It is spread over the invoices
 * being paid **largest invoice first** (`allocateCredits`), and each invoice's charge is what
 * the credit does not cover. What is emitted is exactly what FTE is asked to do: cash lines,
 * the credit pairings, and the on-account amount.
 */
@Component({
  selector: 'app-multi-invoice-selection',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatTableModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatTooltipModule,
    NgxCurrencyDirective,
  ],
  templateUrl: './multi-invoice-selection.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => MultiInvoiceSelectionComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MultiInvoiceSelectionComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MultiInvoiceSelectionComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy, OnChanges {
  @Input() customerId: string | undefined;
  @Input() customerSettings: CustomerSettings | undefined;
  @Input() preSelectedInvoiceId: string | null = null; // Invoice to auto-select on load
  @Input() paymentMode: PaymentMethodMode | undefined; // Payment mode to determine if capital costs should be shown
  @Input() cardFunding: string | undefined; // 'CREDIT' | 'DEBIT' | 'PREPAID' — label + legacy fallback
  @Input() isCreditCard: boolean | undefined; // authoritative credit flag from the stored token
  @Input() isDebitCard: boolean | undefined; // authoritative debit flag from the stored token
  @Input() cardType: string | undefined; // e.g. 'Visa', 'Mastercard' — used for the capital cost label
  @Input() cardZip: string | undefined; // billing ZIP for state-based CoC blocking
  /**
   * Advanced FlashPay send: the user picks which invoices to email, not what to
   * charge. Each ticked invoice goes at its full balance; credits, on-account
   * amounts and Cost of Capital belong to the payer's page, so they are hidden.
   */
  @Input() sendMode = false;

  private customersService = inject(CustomersService);
  private cd = inject(ChangeDetectorRef);
  private userStore = inject(UserStore);
  private paymentCalculatorService = inject(PaymentCalculatorService);
  private cocGuard = inject(CostOfCapitalGuardService);

  /** Open charges, oldest due date first. */
  invoices: InvoiceModel[] = [];
  /**
   * Every credit the customer can spend — credit documents **and** money posted straight to their
   * balance — as `GET /customers/{id}/available-credits` returns them.
   *
   * One list from one read, already filtered server-side to what FTE would accept: an unusable
   * credit never reaches the screen, so there is no listed row an operator can click and be
   * refused. Identity throughout is `creditInvoiceRef` — an invoice ref, or an `FCL…` ledger id —
   * which is the value the apply call takes, so nothing has to be translated on the way out.
   */
  credits: AvailableCredit[] = [];
  balances: CustomerBalanceBreakdown | null = null;
  selectedInvoices: Map<string, InvoiceCheckoutItem> = new Map();
  /**
   * Credits the operator clicked, by `creditInvoiceRef`, in the order they clicked them — which is
   * the order credit is drawn.
   */
  appliedCreditRefs: string[] = [];
  /** Credit used from each applied credit, after allocation. */
  creditUsed = new Map<string, number>();
  creditApplications: CreditApplication[] = [];
  /**
   * Chosen credits this selection would leave partly consumed. Only ever populated when the
   * profile requires a credit to be applied in full — otherwise a remainder is allowed and
   * there is nothing to report.
   */
  creditShortfallList: CreditShortfall[] = [];
  onAccountAmount = 0;
  /**
   * The single payment amount the operator entered, or null when amounts are set per invoice.
   * While set, it is spread over the selected invoices oldest due first (after any credit) and
   * the rest goes on account — or, where the profile does not allow that, is `excessAmount`.
   */
  paymentTotal: number | null = null;
  /** What the payment amount could not place: more than the selection owes, no account to take it. */
  excessAmount = 0;
  isLoading = false;

  // Capital Cost properties
  enableCostOfCapital = false;
  capitalCostEnabled = false;
  capitalCostPercent = 0;
  capitalCostAmount = 0;

  private subscription = new Subscription();
  private onChange: (value: MultiInvoiceCheckoutData | null) => void = () => {};
  private onTouched: () => void = () => {};

  private get settings() {
    return this.userStore.activeProfile()?.profileDetail?.profileAdminSettings;
  }

  /** The profile applies credit memos to payments (`enableApplyCreditMemo`). */
  get creditsEnabled(): boolean {
    return !this.sendMode && this.settings?.enableApplyCreditMemo === true;
  }

  /**
   * The sub-setting: a credit must be spent to the last cent or not at all
   * (`requireFullCreditApplication`). FTE refuses a partial application, so the screen has to
   * say so before the charge rather than relay the refusal after it.
   */
  get requireFullCredit(): boolean {
    return this.creditsEnabled && this.settings?.requireFullCreditApplication === true;
  }

  /** The profile takes payments onto the account (`allowPaymentToAccount`). */
  get onAccountEnabled(): boolean {
    return !this.sendMode && this.settings?.allowPaymentToAccount === true;
  }

  /** The sub-setting: an on-account payment may take the customer past their balance. */
  get aboveBalanceAllowed(): boolean {
    return this.settings?.allowPaymentAboveBalance === true;
  }

  ngOnInit(): void {
    this.updateCapitalCostSettings();

    if (this.enableCostOfCapital) {
      this.capitalCostEnabled = this.getEffectiveCoc().defaultSelected;
    }

    if (this.customerId) {
      this.loadInvoices();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // customerId arrives asynchronously (resolved after the customer is loaded /
    // selected), so reload when it changes to a defined value. The initial value
    // is handled by ngOnInit, so skip the first change to avoid a double fetch.
    if (changes['customerId'] && !changes['customerId'].firstChange && this.customerId) {
      this.loadInvoices();
    }

    // React to payment mode, card classification, or customer CoC settings changes
    if (
      changes['paymentMode'] ||
      changes['cardFunding'] ||
      changes['isCreditCard'] ||
      changes['isDebitCard'] ||
      changes['cardZip'] ||
      changes['customerSettings']
    ) {
      const wasEnabled = this.enableCostOfCapital;
      this.updateCapitalCostSettings();

      if (!this.enableCostOfCapital) {
        // Non-credit payment method or customer CoC off — force toggle off
        if (this.capitalCostEnabled) {
          this.capitalCostEnabled = false;
          this.capitalCostAmount = 0;
          this.emitChanges();
        }
      } else if (!wasEnabled || changes['customerSettings']) {
        this.capitalCostEnabled = this.getEffectiveCoc().defaultSelected;
        if (this.capitalCostEnabled) {
          this.calculateCapitalCost();
        }
        this.emitChanges();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getEffectiveCoc() {
    const profile = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings;
    return resolveCustomerCostOfCapital(profile, this.customerSettings);
  }

  private updateCapitalCostSettings(): void {
    const effective = this.getEffectiveCoc();

    // Capital cost applies to credit card payments only. The authoritative
    // signal is the stored token's isCreditCard flag (populated from
    // DBPaymentToken by the backend). Fall back to the cardFunding string
    // for the new-card-entry path, where we don't yet have a stored token.
    // Prepaid/unknown cards land as !isCreditCard → no surcharge, which
    // matches the product rule (treat prepaid like debit for pricing).
    const isAch = this.paymentMode === PaymentMethodMode.achpay;
    const hasBooleanClassification = this.isCreditCard === true || this.isDebitCard === true;
    const isCreditCardPayment = hasBooleanClassification
      ? this.isCreditCard === true
      : !isAch && this.cardFunding !== 'DEBIT' && this.cardFunding !== 'PREPAID';

    // An Advanced FlashPay send charges nothing: the payer's page adds Cost of
    // Capital for the method they choose.
    this.enableCostOfCapital = !this.sendMode && effective.enabled && !isAch && isCreditCardPayment;
    this.capitalCostPercent = effective.percent;

    // Check state-based CoC block after credit-card eligibility is confirmed
    if (this.enableCostOfCapital && this.cardZip) {
      this.cocGuard.isBlocked(this.cardZip).then((blocked) => {
        if (blocked) {
          this.enableCostOfCapital = false;
          this.capitalCostEnabled = false;
          this.capitalCostAmount = 0;
          this.emitChanges();
          this.cd.markForCheck();
        }
      });
    }
  }

  loadInvoices(): void {
    if (!this.customerId) {
      return;
    }

    this.isLoading = true;
    this.cd.markForCheck();

    const customerId = this.customerId;
    this.subscription.add(
      forkJoin({
        charges: this.customersService.getOpenCustomerDocuments(customerId, PAYABLE_TYPES),
        credits: this.creditsEnabled
          ? this.customersService.getAvailableCredits(customerId).pipe(
              map((r) => r.credits),
              // A failed credit read must not cost the operator the step — they can still take
              // the payment in cash. Logged rather than swallowed, because "no credits" and "the
              // credit read failed" look identical on screen.
              catchError((error) => {
                console.error('Error loading available credits:', error);
                return of([] as AvailableCredit[]);
              })
            )
          : of([] as AvailableCredit[]),
        // The header is informative; a failed read must not cost the operator the step.
        summary: this.customersService.getCustomerDetailSummary(customerId).pipe(catchError(() => of(null))),
      }).subscribe({
        next: ({ charges, credits, summary }) => {
          // A positive balance is owed; a credit type is open while it is negative.
          // Documents an authoritative import stopped listing are left out entirely, both sides:
          // FTE refuses to take money against a NotPayable invoice and refuses to apply a
          // NotUsable credit, so offering either here — even disabled — only invites the question
          // of why it cannot be used. Both keep their balance and stay visible on the customer's
          // Invoices tab, which is where their status and the reason belong. Leaving a NotUsable
          // credit off this screen does mean the credit column and the ledger disagree by its
          // amount; that gap is the known consequence of the ERP having dropped it.
          this.invoices = charges.filter((inv) => (inv.balance ?? 0) > 0 && !this.isNotPayable(inv));
          // No filter: `available-credits` already applied the same usability rule the apply call
          // applies, and returns only credits with something left to spend.
          this.credits = credits;
          this.balances = summary?.balances ?? this.balancesFromDocuments();
          this.selectedInvoices.clear();
          this.appliedCreditRefs = [];
          this.paymentTotal = null;
          this.excessAmount = 0;

          // Auto-select pre-selected invoice if provided. A NotPayable one is not in the list at
          // all, so this simply finds nothing rather than making a selection FTE would refuse.
          if (this.preSelectedInvoiceId) {
            const preSelectedInvoice = this.invoices.find((inv) => inv.invoiceId === this.preSelectedInvoiceId);
            if (preSelectedInvoice) {
              this.toggleInvoiceSelection(preSelectedInvoice, true);
            }
          }

          this.isLoading = false;
          this.recalculate();
          this.cd.markForCheck();
        },
        error: (error) => {
          console.error('Error loading invoices:', error);
          this.isLoading = false;
          this.cd.markForCheck();
        },
      })
    );
  }

  /**
   * The header from the loaded documents alone — only when the summary read failed or predates
   * it. Like FTE's summary, it counts only money that can be collected, so NotPayable is out.
   */
  private balancesFromDocuments(): CustomerBalanceBreakdown {
    const sum = (rows: InvoiceModel[]) => round2(rows.reduce((s, r) => s + (r.balance ?? 0), 0));
    const payable = this.payableInvoices;
    const invoices = payable.filter((i) => !NON_INVOICE_TYPES.has(i.invoiceType ?? ''));
    const nonInvoice = payable.filter((i) => NON_INVOICE_TYPES.has(i.invoiceType ?? ''));
    const overdue = payable.filter((i) => i.status === 'overdue');
    const today = new Date().toDateString();
    const dueToday = payable.filter((i) => i.dueDate && new Date(i.dueDate).toDateString() === today);
    // The credit list is already only what can be applied, so it counts as credit on file
    // whole. Its two kinds are reported separately, the way the summary reports them: a credit
    // document is credit on file, a balance credit is money posted with no document.
    const documentCredits = this.credits.filter((c) => c.source === 'document');
    const balanceCredits = this.credits.filter((c) => c.source === 'balance');
    const creditTotal = round2(documentCredits.reduce((n, c) => n + c.available, 0));
    const balanceCreditTotal = round2(balanceCredits.reduce((n, c) => n + c.available, 0));
    return {
      // Understates a customer carrying an assessed FEE: a fee posted to the balance has no
      // document and is not a credit, so neither list here can see it. Accepted, because this
      // runs only when the summary read failed and the summary is the only thing that knows.
      currentBalance: round2(sum(payable) - creditTotal - balanceCreditTotal),
      invoiceBalance: { amount: sum(invoices), count: invoices.length },
      nonInvoiceBalance: { amount: sum(nonInvoice), count: nonInvoice.length },
      creditsOnFile: { amount: creditTotal, count: documentCredits.length },
      overdue: { amount: sum(overdue), count: overdue.length },
      dueToday: { amount: sum(dueToday), count: dueToday.length },
      balanceEntries: balanceCredits.length
        ? { amount: round2(-balanceCreditTotal), count: balanceCredits.length }
        : undefined,
    };
  }

  typeLabel(doc: InvoiceModel): string | null {
    return TYPE_LABELS[doc.invoiceType ?? ''] ?? null;
  }

  isNonInvoice(doc: InvoiceModel): boolean {
    return NON_INVOICE_TYPES.has(doc.invoiceType ?? '');
  }

  /**
   * The ERP stopped listing this invoice as open and nothing on file explains it as paid.
   * `hasBalance` still returns it (its balance is kept), but FTE refuses any payment against it,
   * so `loadInvoices` keeps it out of the list entirely — it belongs on the customer's Invoices
   * tab, where its status and reason are shown, not on a screen for taking money.
   */
  isNotPayable(doc: InvoiceModel): boolean {
    return doc.status === 'notpayable';
  }

  /**
   * The invoices that can actually be selected. `loadInvoices` has already excluded NotPayable,
   * so this is the whole list — the filter stays as the guard that keeps that true.
   */
  get payableInvoices(): InvoiceModel[] {
    return this.invoices.filter((inv) => !this.isNotPayable(inv));
  }

  /**
   * Money the operator posted straight to the balance, rather than a credit document.
   *
   * Worth telling apart on screen: it carries the reason somebody typed, it has no document
   * number an ERP would recognise, and it is the one kind a distributor can create here.
   */
  isBalanceCredit(credit: AvailableCredit): boolean {
    return credit.source === 'balance';
  }

  /** What to call a credit on its card — its document type, or what it is when there is none. */
  creditLabel(credit: AvailableCredit): string {
    if (credit.source === 'balance') return 'Balance credit';
    return TYPE_LABELS[credit.invoiceType ?? ''] ?? 'Credit';
  }

  /** How a credit is identified on screen: its document number, or its ledger id. */
  creditRefLabel(credit: AvailableCredit): string {
    return credit.creditInvoiceRef;
  }

  isInvoiceSelected(invoice: InvoiceModel): boolean {
    return this.selectedInvoices.has(invoice.invoiceId);
  }

  toggleInvoiceSelection(invoice: InvoiceModel, checked: boolean): void {
    if (checked && this.isNotPayable(invoice)) {
      return;
    }
    if (checked) {
      // Add invoice to selection with default payment amount as outstanding balance
      const outstandingBalance = this.getOutstandingBalance(invoice);
      this.selectedInvoices.set(invoice.invoiceId, {
        invoice,
        paymentAmount: outstandingBalance,
        outstandingBalance,
      });
    } else {
      this.selectedInvoices.delete(invoice.invoiceId);
    }
    this.recalculate();
  }

  selectAll(): void {
    this.payableInvoices.forEach((invoice) => {
      if (!this.selectedInvoices.has(invoice.invoiceId)) {
        const outstandingBalance = this.getOutstandingBalance(invoice);
        this.selectedInvoices.set(invoice.invoiceId, { invoice, paymentAmount: outstandingBalance, outstandingBalance });
      }
    });
    this.recalculate();
  }

  clearSelection(): void {
    this.selectedInvoices.clear();
    this.recalculate();
  }

  // ── Credit memos ─────────────────────────────────────────────────────────

  isCreditApplied(credit: AvailableCredit): boolean {
    return this.appliedCreditRefs.includes(credit.creditInvoiceRef);
  }

  /** Clicking a credit applies it (or stops applying it); the split is recomputed. */
  toggleCredit(credit: AvailableCredit): void {
    // Only a credit this screen is holding. The allocator draws from `this.credits`, so a ref
    // that is not in it would be recorded as applied, allocate nothing, and read as a credit
    // that silently did not work.
    if (!this.credits.some((c) => c.creditInvoiceRef === credit.creditInvoiceRef)) return;
    this.appliedCreditRefs = this.isCreditApplied(credit)
      ? this.appliedCreditRefs.filter((ref) => ref !== credit.creditInvoiceRef)
      : [...this.appliedCreditRefs, credit.creditInvoiceRef];
    this.recalculate();
  }

  creditAvailable(credit: AvailableCredit): number {
    return round2(Math.max(0, credit.available));
  }

  creditUsedAmount(credit: AvailableCredit): number {
    return this.creditUsed.get(credit.creditInvoiceRef) ?? 0;
  }

  /** What this credit would be left holding, when the profile will not allow a remainder. */
  creditShortfallFor(credit: AvailableCredit): number {
    return this.creditShortfallList.find((s) => s.creditInvoiceRef === credit.creditInvoiceRef)?.short ?? 0;
  }

  // ── Apply to account ─────────────────────────────────────────────────────

  /**
   * The most that may go on the account: what the customer still owes once this payment's
   * invoice cash is counted — unless the profile lets payments exceed the balance.
   */
  get onAccountMax(): number | null {
    if (this.aboveBalanceAllowed) return null;
    const balance = this.balances?.currentBalance ?? 0;
    return round2(Math.max(0, balance - this.getInvoiceCashTotal()));
  }

  updateOnAccountAmount(amount: string | number | null): void {
    // While a payment amount is entered the account takes its remainder; the field is read-only.
    if (this.isAmountMode) return;
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount ?? 0;
    this.onAccountAmount = isNaN(numericAmount as number) ? 0 : round2(Math.max(0, numericAmount as number));
    this.recalculate();
  }

  // ── Payment amount ───────────────────────────────────────────────────────

  /**
   * A single payment amount is driving the per-invoice amounts. Never in an Advanced FlashPay send,
   * which requests each ticked invoice at its full balance.
   */
  get isAmountMode(): boolean {
    return !this.sendMode && this.paymentTotal !== null;
  }

  /**
   * The operator entered one amount for the whole payment. It fills the selected invoices oldest
   * due first and the rest goes on account. Clearing it (or 0) goes back to per-invoice amounts,
   * each selected invoice at its full balance again, as if it had just been ticked.
   */
  setPaymentTotal(amount: string | number | null): void {
    if (this.sendMode) {
      return;
    }
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (numericAmount == null || isNaN(numericAmount) || numericAmount <= 0) {
      if (this.isAmountMode) {
        this.paymentTotal = null;
        this.excessAmount = 0;
        this.onAccountAmount = 0;
        this.selectedInvoices.forEach((item) => (item.paymentAmount = item.outstandingBalance));
      }
    } else {
      this.paymentTotal = round2(numericAmount);
    }
    this.recalculate();
  }

  /** A ticked invoice the payment amount does not reach: shown, but not paid. */
  isUnreached(invoice: InvoiceModel): boolean {
    return this.isAmountMode && this.isInvoiceSelected(invoice) && this.getPaymentAmount(invoice) <= 0;
  }

  // ── Amounts ──────────────────────────────────────────────────────────────

  updatePaymentAmount(invoice: InvoiceModel, amount: string | number): void {
    const checkoutItem = this.selectedInvoices.get(invoice.invoiceId);
    if (checkoutItem) {
      // Fine-tuning one invoice by hand ends the payment amount: what is on screen now stands,
      // and is edited like any manual selection from here.
      this.paymentTotal = null;
      this.excessAmount = 0;
      const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
      checkoutItem.paymentAmount = isNaN(numericAmount) ? 0 : numericAmount;
      this.recalculate();
    }
  }

  getPaymentAmount(invoice: InvoiceModel): number {
    return this.selectedInvoices.get(invoice.invoiceId)?.paymentAmount ?? 0;
  }

  getCreditAmount(invoice: InvoiceModel): number {
    return this.selectedInvoices.get(invoice.invoiceId)?.creditAmount ?? 0;
  }

  getCashAmount(invoice: InvoiceModel): number {
    const item = this.selectedInvoices.get(invoice.invoiceId);
    return item?.cashAmount ?? item?.paymentAmount ?? 0;
  }

  getOutstandingBalance(invoice: InvoiceModel): number {
    return invoice.balance ?? invoice.amount;
  }

  /** Everything paid on the selected invoices, credit and cash together. */
  getTotalSelectedAmount(): number {
    let total = 0;
    this.selectedInvoices.forEach((item) => (total += item.paymentAmount));
    return round2(total);
  }

  getCreditsAppliedTotal(): number {
    return round2(this.creditApplications.reduce((s, a) => s + a.amount, 0));
  }

  /** What the charge puts on the selected invoices — what credit does not cover. */
  getInvoiceCashTotal(): number {
    let total = 0;
    this.selectedInvoices.forEach((item) => (total += item.cashAmount ?? item.paymentAmount));
    return round2(total);
  }

  /** Cash before capital cost: invoice cash plus the on-account amount. */
  getCashSubtotal(): number {
    return round2(this.getInvoiceCashTotal() + this.onAccountAmount);
  }

  /** The charge: cash plus capital cost. */
  getTotalAmount(): number {
    return round2(this.getCashSubtotal() + this.capitalCostAmount);
  }

  /** Credit covers everything and nothing is put on the account: there is nothing to charge. */
  get creditCoversAll(): boolean {
    return this.getCreditsAppliedTotal() > 0 && this.getCashSubtotal() === 0;
  }

  toggleCapitalCost(enabled: boolean): void {
    this.capitalCostEnabled = enabled;
    this.recalculate();
  }

  calculateCapitalCost(): void {
    if (!this.capitalCostEnabled || !this.capitalCostPercent) {
      this.capitalCostAmount = 0;
      return;
    }
    // On the cash only — credit applied is not charged to the card.
    const calculated = this.paymentCalculatorService.calculateCapital(this.getCashSubtotal(), this.capitalCostPercent);
    // PaymentCalculatorService.round() returns a string, so we need to parse it
    this.capitalCostAmount = parseFloat(calculated as any) || 0;
  }

  getCapitalCostLabel(): string {
    const base = `Capital Cost ${this.capitalCostPercent}%`;
    if (!this.cardType && !this.cardFunding) {
      return base;
    }
    // Format funding with title case: 'CREDIT' -> 'Credit'
    const funding = this.cardFunding
      ? this.cardFunding.charAt(0).toUpperCase() + this.cardFunding.slice(1).toLowerCase()
      : '';
    const suffix = [this.cardType, funding].filter(Boolean).join(' ').trim();
    return suffix ? `${base} - ${suffix}` : base;
  }

  /**
   * Re-derive everything that follows from the selection: the credit split (largest
   * invoice first), each invoice's cash, capital cost on the cash, and what is emitted.
   */
  private recalculate(): void {
    const selected = Array.from(this.selectedInvoices.values());
    const chosenCredits = this.appliedCreditRefs
      .map((ref) => this.credits.find((c) => c.creditInvoiceRef === ref))
      .filter((c): c is AvailableCredit => !!c)
      .map((c) => ({ creditInvoiceRef: c.creditInvoiceRef, available: this.creditAvailable(c) }));

    let allocation: CreditAllocation;
    if (this.isAmountMode && this.paymentTotal !== null) {
      // A payment amount: credit first, against each invoice's whole balance (largest first, as
      // ever), then the cash fills what credit left, oldest due first. The credit split is not
      // re-run on the result — largest-first over the new amounts could move it.
      allocation = allocateCredits(
        selected.map((item) => ({ invoiceRef: item.invoice.invoiceNumber, amount: item.outstandingBalance })),
        chosenCredits
      );
      const creditOf = (item: InvoiceCheckoutItem) => allocation.creditByInvoice.get(item.invoice.invoiceNumber) ?? 0;
      const oldestFirst = [...selected].sort((a, b) => compareDueDateOldestFirst(a.invoice, b.invoice));
      const cash = allocateOldestFirst(
        oldestFirst.map((item) => ({
          invoiceRef: item.invoice.invoiceNumber,
          amount: round2(item.outstandingBalance - creditOf(item)),
        })),
        this.paymentTotal
      );
      for (const item of selected) {
        item.creditAmount = creditOf(item);
        item.cashAmount = cash.amounts.get(item.invoice.invoiceNumber) ?? 0;
        item.paymentAmount = round2(item.creditAmount + item.cashAmount);
      }
      this.onAccountAmount = this.onAccountEnabled ? cash.remainder : 0;
      this.excessAmount = this.onAccountEnabled ? 0 : cash.remainder;
    } else {
      allocation = allocateCredits(
        selected.map((item) => ({ invoiceRef: item.invoice.invoiceNumber, amount: item.paymentAmount })),
        chosenCredits
      );
      for (const item of selected) {
        const credit = allocation.creditByInvoice.get(item.invoice.invoiceNumber) ?? 0;
        item.creditAmount = credit;
        item.cashAmount = round2(Math.max(0, item.paymentAmount - credit));
      }
    }
    this.creditShortfallList = this.requireFullCredit ? creditShortfalls(allocation, chosenCredits) : [];
    this.creditApplications = allocation.applications;
    this.creditUsed = new Map(
      this.appliedCreditRefs.map((ref) => [ref, allocation.usedByCredit.get(ref) ?? 0])
    );

    this.calculateCapitalCost();
    this.cd.markForCheck();
    this.emitChanges();
  }

  /**
   * Why a selection the operator has started cannot be submitted — shown under the totals,
   * since a disabled Next button alone does not say what is wrong. The empty case has its
   * own prompt.
   */
  get selectionProblem(): string | null {
    if (this.selectedInvoices.size === 0 && !(this.onAccountAmount > 0)) return null;
    return this.currentValidationError();
  }

  /** Why the selection cannot be submitted, or null. */
  private currentValidationError(): string | null {
    if (this.selectedInvoices.size === 0 && !(this.onAccountAmount > 0)) {
      return this.onAccountEnabled
        ? 'Select at least one invoice, or enter an amount to apply to the account.'
        : 'Please select at least one invoice to proceed.';
    }
    if (this.excessAmount > 0.005) {
      return `The amount exceeds the selected invoices by ${this.excessAmount.toFixed(2)}. Select more invoices or lower the amount.`;
    }
    for (const item of this.selectedInvoices.values()) {
      // A payment amount that runs out before the newest invoices leaves them at 0: not paid, not wrong.
      if (item.paymentAmount <= 0 && this.isAmountMode) continue;
      if (item.paymentAmount <= 0) return `Enter an amount to pay on ${item.invoice.invoiceNumber}.`;
      if (item.paymentAmount > item.outstandingBalance + 0.005) {
        return `${item.invoice.invoiceNumber} owes ${item.outstandingBalance.toFixed(2)}; the amount cannot exceed it.`;
      }
    }
    const max = this.onAccountMax;
    if (this.onAccountAmount > 0 && max !== null && this.onAccountAmount > max + 0.005) {
      return `The amount applied to the account cannot exceed ${max.toFixed(2)}, the rest of the customer's balance.`;
    }
    // A credit this profile will not let the operator spend in part. Said here rather than
    // left to FTE's 409 so the operator is told what to change while they can still change it,
    // and named with the shortfall so "select more invoices" is an amount, not a guess.
    const [worst] = this.creditShortfallList;
    if (worst) {
      return `Credit ${worst.creditInvoiceRef} holds ${worst.available.toFixed(2)} and must be applied in full — select at least ${worst.short.toFixed(2)} more in invoices, or clear the credit.`;
    }
    return null;
  }

  private emitChanges(): void {
    this.onTouched();

    if (this.selectedInvoices.size === 0 && !(this.onAccountAmount > 0)) {
      this.onChange(null);
      return;
    }

    const checkoutData: MultiInvoiceCheckoutData = {
      // Invoices a payment amount did not reach stay ticked on screen but are not part of the payment.
      invoices: Array.from(this.selectedInvoices.values()).filter((item) => !this.isAmountMode || item.paymentAmount > 0),
      totalAmount: this.getTotalAmount(),
      customerId: this.customerId ?? '',
      customerName: '', // Will be set by parent component
      capitalCostEnabled: this.capitalCostEnabled,
      capitalCostPercent: this.capitalCostPercent,
      capitalCostAmount: this.capitalCostAmount,
      creditApplications: this.creditApplications,
      creditsAppliedAmount: this.getCreditsAppliedTotal(),
      onAccountAmount: this.onAccountAmount,
      cashSubtotal: this.getCashSubtotal(),
      paymentTotal: this.isAmountMode ? this.paymentTotal : null,
      validationError: this.currentValidationError(),
    };

    this.onChange(checkoutData);
  }

  // ControlValueAccessor implementation
  writeValue(value: MultiInvoiceCheckoutData | null): void {
    this.selectedInvoices.clear();
    if (value) {
      value.invoices.forEach((item) => this.selectedInvoices.set(item.invoice.invoiceId, item));
      this.onAccountAmount = value.onAccountAmount ?? 0;
      this.paymentTotal = value.paymentTotal ?? null;
    }
    this.cd.markForCheck();
  }

  registerOnChange(fn: (value: MultiInvoiceCheckoutData | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    const error = this.currentValidationError();
    return error ? { invalidSelection: error } : null;
  }
}
